/* eslint semi:0 */
/**
 * A way to query campaign configuration, which is delivered via PDO.
 */
import { intersection } from '../utils/set'
import { warn } from '../application/console'
import { queryString as qs } from '../utils/window'
import { getCountryCode } from '../providers/geolocationProvider'
import { normalizeCountryCode, normalizeLocale } from '../utils/campaign'
import { getInfluencerId } from '../utils/pageContext'

import {
  getIsNativeApp,
  getIsFacebookApp,
  path,
  getIsMobile,
  getIsTablet,
  getIsDesktop,
} from '../utils/ua'
import {
  getPageType,
  getIsHybrid,
  getIsSlideshow,
  getIsTopX,
  getPageLocale,
  isSupportedForDomain,
  getPostCategories,
} from '../application/pageDataObject'

const { isEmbedded } = path
const affiliateId = getInfluencerId()
const FEATURE_FLAGS = {
  currentTime: qs.currentTime,
}

let currentTime
function getCurrentTime() {
  return currentTime || (currentTime = FEATURE_FLAGS.currentTime || new Date())
}

function testLocale({ locales }) {
  if (!locales) {
    return true
  }
  return locales.map(normalizeLocale).includes(normalizeLocale(getPageLocale()))
}

function testPageType({ pageTypes }) {
  if (!pageTypes) {
    return true
  }
  return pageTypes.includes(getPageType())
}

function testAffiliateIdsWhiteList({ affiliateIdsWhiteList }) {
  if (!affiliateIdsWhiteList) {
    return true
  }
  if (!affiliateId) {
    return false
  }
  const normalizedList = affiliateIdsWhiteList.map(String)
  return normalizedList.includes(affiliateId)
}

function testSupportedForDomain({ featureId }) {
  if (!featureId) {
    return true
  }
  return isSupportedForDomain(featureId)
}

function testBoolean(campaignField, booleanToCompare) {
  if (campaignField === undefined) {
    return true
  }
  if (booleanToCompare === undefined) {
    warn('booleanToCompare should not be undefind. something is wrong')
    return true
  }
  return campaignField === booleanToCompare
}

function testSlideShow(campaign) {
  return testBoolean(campaign.isSlideshow, getIsSlideshow())
}

function testTopX(campaign) {
  return testBoolean(campaign.isTopX, getIsTopX())
}

function testMobile(campaign) {
  return testBoolean(campaign.isMobile, getIsMobile())
}

function testTablet(campaign) {
  return testBoolean(campaign.isTablet, getIsTablet())
}

function testDesktop(campaign) {
  return testBoolean(campaign.isDesktop, getIsDesktop())
}

function testHybrid(campaign) {
  return testBoolean(campaign.isHybrid, getIsHybrid())
}

function testNativeApp(campaign) {
  return testBoolean(campaign.isNativeApp, getIsNativeApp())
}

function testFacebookApp(campaign) {
  return testBoolean(campaign.isFacebookApp, getIsFacebookApp())
}

function testEmbedded(campaign) {
  return testBoolean(campaign.isEmbedded, isEmbedded)
}

function testCurrentDayEnabled({ daysWhiteList }) {
  if (!daysWhiteList) {
    return true
  }
  const currentDay = new Date().getDay()
  return daysWhiteList.includes(currentDay)
}

function testIsntInBlacklistedCategories({ blacklistedCategories }) {
  if (!Array.isArray(blacklistedCategories) || !blacklistedCategories.length) {
    return true
  }
  return intersection(blacklistedCategories, getPostCategories()).size === 0
}

function testIsntInWhitelistedCategories({ whitelistedCategories }) {
  if (!Array.isArray(whitelistedCategories) || !whitelistedCategories.length) {
    return true
  }
  return intersection(whitelistedCategories, getPostCategories()).size > 0
}

function doesRequireGeo({ whiteListCountryCodes, blackListCountryCodes, countryCodesToTimeSpan }) {
  return whiteListCountryCodes || blackListCountryCodes || countryCodesToTimeSpan
}

function isInTimeframeByCountryCode(cc, time, { start, end, countryCodes }) {
  if (!countryCodes.map(normalizeCountryCode).includes(cc)) {
    return false
  }
  return time >= new Date(start) && time <= new Date(end)
}

function isCountryCodesToTimeSpanEnabled(campaign, cc) {
  const { countryCodesToTimeSpan } = campaign
  if (!countryCodesToTimeSpan) {
    return true
  }
  const isCurrentCountryCodeInTimeSpan = countryCodesToTimeSpan.some(
    isInTimeframeByCountryCode.part(cc, getCurrentTime())
  )
  return isCurrentCountryCodeInTimeSpan
}

function isGeoWhiteListEnabled(campaign, cc) {
  if (!campaign.whiteListCountryCodes) {
    return true
  }
  return campaign.whiteListCountryCodes.map(normalizeCountryCode).includes(cc)
}

function isGeoBlackListEnabled(campaign, cc) {
  if (!campaign.blackListCountryCodes) {
    return true
  }
  return !campaign.blackListCountryCodes.map(normalizeCountryCode).includes(cc)
}

function isGeoEnabled(campaign, cc) {
  return isGeoWhiteListEnabled(campaign, cc) && isGeoBlackListEnabled(campaign, cc)
}

const SYNC_TESTS = [
  testFacebookApp,
  testNativeApp,
  testMobile,
  testTablet,
  testDesktop,
  testEmbedded,
  testCurrentDayEnabled,
  testTopX,
  testHybrid,
  testSlideShow,
  testPageType,
  testLocale,
  testSupportedForDomain,
  testAffiliateIdsWhiteList,
  testIsntInBlacklistedCategories,
  testIsntInWhitelistedCategories,
]

export function isCampaignEnabled(campaign) {
  const partiallyApplyCampaign = test => test.bind(0, campaign)
  const syncTests = SYNC_TESTS.map(partiallyApplyCampaign)
  const allPass = syncTests.every(test => test())
  const geoTests = [isGeoEnabled, isCountryCodesToTimeSpanEnabled].map(partiallyApplyCampaign)
  return new Promise(function isCampaignEnabledPromise(resolve, reject) {
    const disableCampaign = reason => resolve({ isCampaignEnabled: false, campaign, reason })
    const enableCampaign = reason => resolve({ isCampaignEnabled: true, campaign, reason })
    if (!allPass) {
      return disableCampaign('sync test(s) failed')
    }
    if (!doesRequireGeo(campaign)) {
      return enableCampaign('does not require geoLocation')
    }
    return getCountryCode()
      .then(normalizeCountryCode)
      .then(function verifyGeoTests(cc) {
        return geoTests.every(t => t(cc))
          ? enableCampaign('geo location requirements met')
          : disableCampaign('geo test(s) failed')
      })
      .catch(function(err) {
        const errorMsg = `cannot get country code: ${err}`
        warn(errorMsg)
        return disableCampaign(errorMsg)
      })
  })
}



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/config/campaignConfig.js